import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IInfiniteGetNodeContentQueryData } from '@/api/nodes/queries/get-node-content-query'
import { updateInfiniteNodesOptimistically } from '@/api/nodes/helpers/update-infinite-nodes-optimistically'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { QueryKey } from '@tanstack/react-query'
import { toast } from '@/features/toast/toast'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

interface IToggleNodeFavoriteMutationParams {
    organizationId: string
    brandSlug: string
    node: INode
}

type IToggleNodeFavoriteApiPayload = {
    nodeUuid: string
}

type IToggleNodeFavoriteMutationPayload = Pick<IToggleNodeFavoriteMutationParams, 'node'>

export class ToogleNodeFavoriteMutation {
    static call(api: IApi, { organizationId, brandSlug, ...payload }: IToggleNodeFavoriteMutationParams) {
        return api.put<unknown, IToggleNodeFavoriteApiPayload>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/node/toggle-favorite`,
            { nodeUuid: payload.node.uuid }
        )
    }
}

interface IUseToggleNodeFavoriteMutationParams {
    nodeContentQueryKey: QueryKey
}

export const useToggleNodeFavoriteMutation = ({ nodeContentQueryKey }: IUseToggleNodeFavoriteMutationParams) => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (payload: IToggleNodeFavoriteMutationPayload) => {
            const promise = ToogleNodeFavoriteMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            })

            let prev: IInfiniteGetNodeContentQueryData | undefined

            const wasFavorite = payload.node.isUserFavorite

            updateInfiniteNodesOptimistically({
                qc,
                nodeContentQueryKey,
                onSuccess: () => toast.success(wasFavorite ? 'Removed from favorites' : 'Added to favorites'),
                assignPrev: (data) => {
                    prev = data
                },

                reducePage: (acc, node) => {
                    if (node.uuid === payload.node.uuid) {
                        return [
                            ...acc,
                            {
                                ...node,
                                isUserFavorite: !wasFavorite,
                            },
                        ]
                    }

                    return [...acc, node]
                },

                count: 1,
            })

            return promise
                .then((res) => {
                    // Invalidation promise must be returned
                    return qc
                        .invalidateQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })
                        .then(() => res)
                        .catch(() => res)
                })
                .catch((e) => {
                    toast.error(`Failed to ${wasFavorite ? 'remove from' : 'add to'} favorites`)

                    qc.setQueryData<IInfiniteGetNodeContentQueryData>(nodeContentQueryKey, prev)
                    qc.refetchQueries({
                        predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                    })

                    throw e
                })
        },
    })
}
